// src/stores/lib/TrackManager.ts
import { RendererState } from '../../renderer/RendererState';
import { ElementState } from '../../renderer/ElementState';
import { deepClone } from '../../utility/deepClone';
import VideoCreatorStore from '../VideoCreatorStore';
import { KARAOKE_TRACK_NUMBER } from '../VideoCreatorStore';
import { ensureElementZPositions } from '../../utility/general';

// TODO: This should be... somewhere else also.
import {
  getElementPositionProperties,
  getOriginalVideoTrackSourceDiff,
} from '../../utility/timeline';

export class TrackManager {
  videoCreator: VideoCreatorStore;

  constructor(videoCreator: VideoCreatorStore) {
    this.videoCreator = videoCreator;
  }

  get videoSourceStore() {
    return this.videoCreator.rootStore.videoSourceStore;
  }

  updateFrameLockedTracksAfterRearrange(
    // Support source or renderer state
    oldStateElements: ElementState[] | Record<string, any>[],
    newStateElements: ElementState[] | Record<string, any>[],
  ) {
    this.videoCreator.frameLockedTracks = this.videoCreator.frameLockedTracks
      .map((track) => {
        const elementsInOldSource = oldStateElements.filter(
          (el) => el.track === track,
        );
        if (elementsInOldSource.length > 0) {
          const elementInNewSource = newStateElements.find((el) =>
            elementsInOldSource.find(
              (oldEl) =>
                // Handle element.id or element.source.id
                (oldEl.source?.id && oldEl.source.id === el.source.id) ||
                oldEl.id === el.id,
            ),
          );
          return elementInNewSource?.track;
        }
      })
      .filter((track) => track !== undefined) as number[];
  }

  async rearrangeTracks(
    track: number,
    direction: 'up' | 'down',
  ): Promise<void> {
    if (track === KARAOKE_TRACK_NUMBER) return;

    const renderer = this.videoCreator.renderer;
    if (!renderer || !renderer.state) {
      return;
    }

    // The track number to swap with
    const targetTrack = direction === 'up' ? track + 1 : track - 1;
    if (targetTrack < 1) {
      return;
    }

    // Elements at provided track
    const elementsCurrentTrack = renderer.state.elements.filter(
      (element) => element.track === track,
    );
    if (elementsCurrentTrack.length === 0) {
      return;
    }

    this.videoCreator.saveVideoStateSnapshot(undefined, `rearranging tracks`);

    await this.moveTrackPastAnother(track, targetTrack);
  }

  async lockVideoTrackOnStateChange(elements: ElementState[]) {
    if (elements.length === 0) return;
    if (this.isVideoTrackLocked(elements)) return;
    const activeElement = this.videoCreator.getActiveElement();

    if (
      !activeElement ||
      (activeElement &&
        !this.videoCreator.isOriginalVideoElement(activeElement.source))
    ) {
      await this.lockVideoTrack(elements);
    }
  }

  async lockVideoTrack(elements?: ElementState[]) {
    const videoElements = elements?.filter((e) =>
      this.videoCreator.isOriginalVideoElement(e.source),
    );
    const modifications = {} as Record<string, any>;

    for (let e of videoElements || []) {
      modifications[`${e.source.id}.locked`] = true;
    }
    if (Object.keys(modifications).length > 0) {
      await this.videoCreator.rootStore.videoSourceStore.applyModifications(
        modifications,
      );
    }
  }

  async unLockVideoTrack() {
    const elements = this.videoSourceStore.source.elements;

    const videoElements = elements?.filter((e: Record<string, any>) =>
      this.videoCreator.isOriginalVideoElement(e),
    );
    if (videoElements?.every((e: Record<string, any>) => !e.locked)) return;

    const modifications = {} as Record<string, any>;

    for (let e of videoElements || []) {
      if (e.locked) {
        modifications[`${e.id}.locked`] = false;
      }
    }

    if (Object.keys(modifications).length > 0) {
      await this.videoCreator.rootStore.videoSourceStore.applyModifications(
        modifications,
      );
    }
  }

  isVideoTrackLocked(elements?: ElementState[]) {
    const videoElements = elements?.filter((e) =>
      this.videoCreator.isOriginalVideoElement(e.source),
    );
    return videoElements?.every((e) => e.source.locked);
  }

  toggleTrackFrameLock(track: number) {
    this.videoCreator.frameLockedTracks =
      this.videoCreator.frameLockedTracks.includes(track)
        ? this.videoCreator.frameLockedTracks.filter((t) => t !== track)
        : [...this.videoCreator.frameLockedTracks, track];
  }

  async applyChangesToFrameLockedTracks(
    prevState: RendererState,
    newState: RendererState,
  ) {
    const frameLockedTrackChanges = getOriginalVideoTrackSourceDiff(
      this.videoCreator,
      prevState.elements,
      newState.elements,
    );
    if (Object.keys(frameLockedTrackChanges).length !== 1) {
      console.log(
        'No frame locked tracks changed or more than one, not applying',
      );
      return false;
    }
    const id = Object.keys(frameLockedTrackChanges)[0];
    const trackChanges = getElementPositionProperties(
      this.videoCreator.time,
      newState.elements.find((el) => el.source.id === id)!,
    );
    const modificationObject: any = {};
    for (const element of newState.elements.filter(
      (el) => this.videoCreator.frameLockedTracks.includes(el.track),
      // && el.source.id !== id, PLAT-1415 hotfix to not ensure all clips get updated since applyModifications captures old state before debounced refresh
    )) {
      for (const key in trackChanges) {
        modificationObject[`${element.source.id}.${key}`] =
          trackChanges[key] || null;
      }
      modificationObject[`${element.source.id}.clip`] = true;
    }
    if (Object.keys(modificationObject).length > 0) {
      await this.videoCreator.rootStore.videoSourceStore.applyModifications(
        modificationObject,
      );
      return true;
    }
    return false;
  }

  async moveTrackPastAnother(
    track: number,
    anotherTrack: number,
    modifiedSource: Record<string, any> | undefined = undefined,
  ): Promise<void> {
    const trackDiff = track - anotherTrack;
    const renderer = this.videoCreator.renderer;
    if (
      !renderer ||
      !renderer.state ||
      trackDiff === 0 ||
      track === KARAOKE_TRACK_NUMBER
    ) {
      return;
    }

    const oldStateElements = deepClone(renderer.state.elements);
    const source =
      modifiedSource ||
      deepClone(this.videoCreator.rootStore.videoSourceStore.source);

    const direction = trackDiff > 0 ? 'down' : 'up';
    for (let i = 0; i < Math.abs(trackDiff); i++) {
      // The track number to swap with
      const targetTrack = direction === 'up' ? track + 1 : track - 1;
      if (targetTrack < 1) {
        break;
      }
      // Swap track numbers
      for (const element of source.elements) {
        if (element.track === track) {
          element.track = targetTrack;
        } else if (element.track === targetTrack) {
          element.track = track;
        }
      }
      track = direction === 'up' ? track + 1 : track - 1;
    }

    // Set source by the mutated state
    const newSource =
      this.videoCreator.videoTranscriptionProcessor.adjustTrackNumbersToStartFromOne(
        source,
      );

    // Apply ensureElementZPositions to update z-index values based on new track numbers
    const updatedSource = ensureElementZPositions(newSource);

    await this.videoCreator.rootStore.videoSourceStore.setSource(
      updatedSource,
      true,
    );
    this.updateFrameLockedTracksAfterRearrange(
      oldStateElements,
      renderer.state.elements,
    );
    this.videoCreator.createDefaultUndoPoint();
  }
}
